import { useState, useEffect } from 'react'
import { supabase } from '@/integrations/supabase/client'

interface RecentModule {
  id: string
  title: string
  description: string
  category: string
  last_accessed: string
}

export const useRecentModules = () => {
  const [recentModules, setRecentModules] = useState<RecentModule[]>([])
  const [loading, setLoading] = useState(true)

  const fetchRecentModules = async () => {
    try {
      // Clear existing modules first to prevent stale data
      setRecentModules([])
      
      const { data: { session } } = await supabase.auth.getSession()
      if (!session?.user?.id) {
        setLoading(false)
        return
      }

      // Get recently accessed modules from user_content_progress where content was actually completed
      const { data: progressData, error } = await supabase
        .from('user_content_progress')
        .select(`
          content_id,
          updated_at,
          completed,
          learn_content!inner(
            module_id,
            learn_modules!inner(
              id,
              title,
              description,
              category
            )
          )
        `)
        .eq('user_id', session.user.id)
        .eq('completed', true)  // Only get completed content
        .order('updated_at', { ascending: false })

      if (error) throw error

      // Extract unique modules and sort by most recent access
      const moduleMap = new Map<string, RecentModule>()
      
      progressData?.forEach((progress) => {
        const module = progress.learn_content.learn_modules
        if (module && !moduleMap.has(module.id)) {
          moduleMap.set(module.id, {
            id: module.id,
            title: module.title,
            description: module.description,
            category: module.category,
            last_accessed: progress.updated_at
          })
        }
      })

      const modules = Array.from(moduleMap.values())
        .sort((a, b) => new Date(b.last_accessed).getTime() - new Date(a.last_accessed).getTime())
        .slice(0, 3)

      setRecentModules(modules)
    } catch (error) {
      console.error('Error fetching recent modules:', error)
      // Clear modules on error to prevent stale data
      setRecentModules([])
    } finally {
      setLoading(false)
    }
  }

  const addRecentModule = async (module: Omit<RecentModule, 'last_accessed'>) => {
    try {
      const { data: { session } } = await supabase.auth.getSession()
      if (!session?.user?.id) return

      // Get the first content item for this module
      const { data: contentData, error: contentError } = await supabase
        .from('learn_content')
        .select('id')
        .eq('module_id', module.id)
        .order('order_index')
        .limit(1)
        .single()

      if (contentError) throw contentError

      // Create or update progress record - don't mark as completed, just track interaction
      const { error: progressError } = await supabase
        .from('user_content_progress')
        .upsert({
          user_id: session.user.id,
          content_id: contentData.id,
          completed: false,  // Don't mark as completed just for interaction
          completed_at: null,
          updated_at: new Date().toISOString()
        }, {
          onConflict: 'user_id,content_id'
        })

      if (progressError) throw progressError

      // Refresh the list
      await fetchRecentModules()
    } catch (error) {
      console.error('Error adding recent module:', error)
    }
  }

  // Set up auth state change listener to clear modules when user signs out
  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event) => {
      if (event === 'SIGNED_OUT') {
        setRecentModules([])
      }
    })

    return () => {
      subscription.unsubscribe()
    }
  }, [])

  // Initial fetch
  useEffect(() => {
    fetchRecentModules()
  }, [])

  return {
    recentModules,
    loading,
    addRecentModule,
    refreshRecentModules: fetchRecentModules
  }
}
